import { LogLevel } from '@azure/msal-browser';
 
const TENANT_CODE_STORAGE_KEY = 'tenantCode';
const RESERVED_ROUTE_CODES = new Set([
  'absenteeism-overview',
  'absentees-reports',
  'announcement',
  'applicationconfigration',
  'application-configuration',
  'approval-stages',
  'assign-policy',
  'assign-schedule',
  'assign-temporary-schedule',
  'attendance-reports',
  'audit-report',
  'change-authentication',
  'company',
  'dashboard',
  'define-break-definition',
  'define-break-duration',
  'definedelegation',
  'define-delegation',
  'define-holiday-period',
  'define-period',
  'define-ramdan-period',
  'define-schedule',
  'define-timetable',
  'define-work-schedule',
  'designation',
  'employee',
  'employee-exception',
  'employeeleave',
  'employee-reports',
  'employees',
  'error',
  'form-builder',
  'generate-report',
  'group-builder',
  'hr-dashboard',
  'hr-leave-request',
  'hr-remote-work',
  'hr-subordinates-requests',
  'integration-builder',
  'job-grade',
  'leave-builder',
  'leave-request',
  'leave-requests',
  'leave-setting',
  'line-listing-reports',
  'lookup',
  'manager-delegation',
  'manual-entry',
  'manualentrybuilder',
  'manualentry-builder',
  'manual-entry-request',
  'missing-gate-transaction',
  'nationality',
  'new-form-builder',
  'new-report-builder',
  'notification-template',
  'notification-tracking',
  'permission-request',
  'permission-setting',
  'permission-type',
  'policy',
  'reader',
  'recalculate-attendance',
  'religion',
  'remote-setting',
  'remote-work',
  'remote-work-request',
  'remot-work-builder',
  'report-builder',
  'requests-reports',
  'schedule-assignment',
  'self-service-permission-request',
  'shift-work-schedule',
  'subordinates-requests',
  'ta-policy',
  'third-party',
  'tracking-employee-request',
  'transaction',
  'tutorial',
  'unauthorized',
  'update-bulk-employee',
  'update-supervisor',
  'user-role-assignment',
  'user-type',
  'view-form-builder',
  'view-work-flow-application-list',
  'violations-reports',
  'work-flow-application',
  'work-flow-builder',
  'work-location',
  'work-schedule-report',
]);

const isReservedRouteCode = (tenantCode) => {
  return RESERVED_ROUTE_CODES.has(String(tenantCode || '').trim().toLowerCase());
};

export const setTenantCode = (tenantCode) => {
  try {
    const normalizedTenantCode = String(tenantCode || '').trim();
    if (!normalizedTenantCode) {
      localStorage.removeItem(TENANT_CODE_STORAGE_KEY);
      return;
    }
    if (isReservedRouteCode(normalizedTenantCode)) return;
    localStorage.setItem(TENANT_CODE_STORAGE_KEY, normalizedTenantCode);
  } catch {}
};

export const getTenantCode = () => {
  try {
    const tenantCode = localStorage.getItem(TENANT_CODE_STORAGE_KEY) || '';
    if (isReservedRouteCode(tenantCode)) {
      localStorage.removeItem(TENANT_CODE_STORAGE_KEY);
      return '';
    }
    return tenantCode;
  } catch {
    return '';
  }
};

const readAzureTenantConfig = () => {
  try {
    const raw = localStorage.getItem('azureTenantConfig');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const buildMsalConfig = () => {
  const azureConfig = readAzureTenantConfig() || {};

  const normalizedConfig = azureConfig?.data && (azureConfig?.clientID == null && azureConfig?.tenantID == null)
    ? azureConfig.data
    : azureConfig;

  const clientId =
    normalizedConfig?.clientID ||
    normalizedConfig?.clientId ||
    '';
  const tenantId =
    normalizedConfig?.tenantID ||
    normalizedConfig?.tenantId ||
    'common';

  // IMPORTANT: redirectUri must match what is configured in Azure App Registration.
  // The configured redirectUrl points at a fixed environment (e.g. production).
  // If we used it while running on a different origin (e.g. localhost), Microsoft
  // would send the browser to that other origin after login, where the MSAL
  // sessionStorage state from this origin doesn't exist -> login silently fails.
  // So only honor it when it matches the current origin; otherwise use the
  // current origin (e.g. http://localhost:3000, which must also be registered
  // in the Azure App Registration).
  const configuredRedirectUri =
    normalizedConfig?.redirectUrl ||
    normalizedConfig?.redirectUri ||
    '';
  let redirectUri = window.location.origin;
  if (configuredRedirectUri) {
    try {
      if (new URL(configuredRedirectUri).origin === window.location.origin) {
        redirectUri = configuredRedirectUri;
      }
    } catch {}
  }

  return {
    auth: {
      clientId: clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: redirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: true,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            default:
              return;
          }
        },
      },
    },
  };
};

export const msalConfig = buildMsalConfig();

export const loginRequest = {
  scopes: ['User.Read'],
};

export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me', //e.g. https://graph.microsoft.com/v1.0/me
};
